import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { formatPunchTime } from '../lib/timeFormat';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Calendar, Upload, Clock, CheckCircle, XCircle, Coffee, AlertTriangle, TrendingUp, Timer, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function MyAttendances() {
  const [attendances, setAttendances] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [file, setFile] = useState(null);
  const [reason, setReason] = useState('');
  const [dateRange, setDateRange] = useState({
    start_date: format(new Date(new Date().setDate(1)), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd')
  });

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchAttendances();
    fetchMonthlySummary();
  }, []);

  const fetchAttendances = async () => {
    try {
      const response = await api.get('/employee/attendances', {
        params: dateRange
      });
      setAttendances(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlySummary = async () => {
    try {
      const response = await api.get('/employee/attendances/monthly-summary', {
        params: { month: currentMonth, year: currentYear }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Summary fetch failed:', error);
    }
  };

  const handleFilter = () => {
    setLoading(true);
    fetchAttendances();
  };

  const handleUploadJustification = async (e) => {
    e.preventDefault();
    if (!reason) {
      toast.error('Motif requis');
      return;
    }
    // Synthetic absences use the absence-justification endpoint (file is optional)
    if (selectedAttendance?.synthetic) {
      try {
        const formData = new FormData();
        if (file) formData.append('file', file);
        await api.post(
          `/attendance/absence-justification?absence_date=${selectedAttendance.date}&reason=${encodeURIComponent(reason)}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success('Justification d\'absence envoyée');
        setOpen(false);
        resetForm();
        fetchAttendances();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
      }
      return;
    }
    // Real attendance with late/absent flag → upload file (required)
    if (!file) {
      toast.error('Fichier requis');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(
        `/attendance/justification/upload?attendance_id=${selectedAttendance.id}&reason=${encodeURIComponent(reason)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Justificatif envoyé avec succès');
      setOpen(false);
      resetForm();
      fetchAttendances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const openJustifyDialog = (attendance) => {
    setSelectedAttendance(attendance);
    setOpen(true);
  };

  const resetForm = () => {
    setFile(null);
    setReason('');
    setSelectedAttendance(null);
  };

  const getStatusBadge = (att) => {
    const status = att?.status;
    const justif = att?.justification_status;
    if (att?.is_absent || status === 'absent') {
      if (justif === 'approved') return <Badge className="bg-blue-500 text-white">Justifiée</Badge>;
      if (justif === 'pending')  return <Badge className="bg-amber-500 text-white">En attente</Badge>;
      if (justif === 'rejected') return <Badge className="bg-red-700 text-white">Refusée</Badge>;
      return <Badge className="bg-error text-white">Absent</Badge>;
    }
    const variants = {
      present: { label: 'Présent', className: 'bg-success text-white' },
      late: { label: 'Retard', className: 'bg-warning text-white' },
      half_day: { label: 'Demi-journée', className: 'bg-slate-500 text-white' }
    };
    const variant = variants[status] || variants.present;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  const getJustificationBadge = (justification_status) => {
    if (!justification_status || justification_status === 'pending') {
      return <Badge className="bg-warning text-white">En attente</Badge>;
    }
    if (justification_status === 'approved') {
      return <Badge className="bg-success text-white"><CheckCircle className="w-3 h-3 mr-1 inline" />Approuvé</Badge>;
    }
    return <Badge className="bg-error text-white"><XCircle className="w-3 h-3 mr-1 inline" />Rejeté</Badge>;
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="my-attendances-page">
      <div>
        <h1 className="font-heading font-bold text-3xl text-slate-900">Mes Presences</h1>
        <p className="text-slate-600 mt-1">Historique et justificatifs</p>
      </div>

      {summary && (
        <div data-testid="monthly-summary">
          <h2 className="text-lg font-semibold text-slate-700 mb-3">
            Recapitulatif du mois ({summary.month}/{summary.year})
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <Card className="border-slate-200" data-testid="summary-present">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <UserCheck className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-slate-500 font-medium">Jours presents</span>
                </div>
                <p className="text-2xl font-bold text-green-700">{summary.total_present}</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="summary-late">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  <span className="text-xs text-slate-500 font-medium">Retards</span>
                </div>
                <p className="text-2xl font-bold text-orange-600">{summary.total_late}</p>
                {summary.total_late_minutes > 0 && (
                  <p className="text-xs text-orange-400">{summary.total_late_minutes} min total</p>
                )}
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="summary-hours">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Timer className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-slate-500 font-medium">Heures travaillees</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{summary.total_hours_worked}h</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="summary-overtime">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs text-slate-500 font-medium">Heures sup.</span>
                </div>
                <p className="text-2xl font-bold text-emerald-700">{summary.total_overtime_hours}h</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200" data-testid="summary-break">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Coffee className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-slate-500 font-medium">Depassement pause</span>
                </div>
                <p className="text-2xl font-bold text-amber-600">{summary.total_break_overtime_minutes} min</p>
                {summary.total_break_duration_minutes > 0 && (
                  <p className="text-xs text-slate-400">{summary.total_break_duration_minutes} min pause total</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Filtrer par période</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Date début</Label>
              <Input
                id="start_date"
                type="date"
                value={dateRange.start_date}
                onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">Date fin</Label>
              <Input
                id="end_date"
                type="date"
                value={dateRange.end_date}
                onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                data-testid="end-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button onClick={handleFilter} className="w-full bg-primary" data-testid="filter-button">
                Filtrer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Historique des Présences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Arrivée</TableHead>
                  <TableHead className="font-semibold">Déb. Pause</TableHead>
                  <TableHead className="font-semibold">Fin Pause</TableHead>
                  <TableHead className="font-semibold">Départ</TableHead>
                  <TableHead className="font-semibold">Heures</TableHead>
                  <TableHead className="font-semibold">Retard</TableHead>
                  <TableHead className="font-semibold">Pause</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                  <TableHead className="font-semibold text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attendances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-slate-500 py-8">
                      Aucune présence trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  attendances.map((att) => (
                    <TableRow key={att.id} data-testid={`attendance-row-${att.id}`} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{att.date}</TableCell>
                      <TableCell data-testid={`checkin-${att.id}`}>
                        {att.check_in ? (
                          <div className="flex items-center gap-1 font-mono text-sm">
                            <Clock className="w-3 h-3 text-green-500" />
                            {formatPunchTime(att.check_in)}
                          </div>
                        ) : <span className="text-slate-300">-</span>}
                      </TableCell>
                      <TableCell data-testid={`break-start-${att.id}`}>
                        {att.break_start ? (
                          <div className="flex items-center gap-1 font-mono text-sm">
                            <Coffee className="w-3 h-3 text-amber-500" />
                            {formatPunchTime(att.break_start)}
                          </div>
                        ) : <span className="text-slate-300">-</span>}
                      </TableCell>
                      <TableCell data-testid={`break-end-${att.id}`}>
                        {att.break_end ? (
                          <div className="flex items-center gap-1 font-mono text-sm">
                            <Coffee className="w-3 h-3 text-amber-500" />
                            {formatPunchTime(att.break_end)}
                          </div>
                        ) : <span className="text-slate-300">-</span>}
                      </TableCell>
                      <TableCell data-testid={`checkout-${att.id}`}>
                        {att.check_out ? (
                          <div className="flex items-center gap-1 font-mono text-sm">
                            <Clock className="w-3 h-3 text-red-500" />
                            {formatPunchTime(att.check_out)}
                          </div>
                        ) : <span className="text-slate-300">-</span>}
                      </TableCell>
                      <TableCell className="font-medium" data-testid={`hours-${att.id}`}>
                        {(att.hours_worked || 0).toFixed(2)}h
                      </TableCell>
                      <TableCell data-testid={`late-${att.id}`}>
                        {att.is_late ? (
                          <div className="flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3 text-orange-500" />
                            <span className="text-orange-600 text-sm font-medium">{att.late_minutes || 0} min</span>
                          </div>
                        ) : <span className="text-green-600 text-sm">OK</span>}
                      </TableCell>
                      <TableCell data-testid={`break-info-${att.id}`}>
                        {att.break_duration_minutes > 0 ? (
                          <div className="text-sm">
                            <span>{att.break_duration_minutes} min</span>
                            {att.break_overtime_minutes > 0 && (
                              <div className="text-red-500 text-xs font-medium">+{att.break_overtime_minutes} min</div>
                            )}
                          </div>
                        ) : <span className="text-slate-300">-</span>}
                      </TableCell>
                      <TableCell>{getStatusBadge(att)}</TableCell>
                      <TableCell className="text-right">
                        {/* Synthetic absence: show "Justifier" if not yet submitted, or status badge */}
                        {att.synthetic && !att.justification_status && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openJustifyDialog(att)}
                            data-testid={`justify-absence-${att.date}`}
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            Justifier
                          </Button>
                        )}
                        {att.synthetic && att.justification_status === 'rejected' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openJustifyDialog(att)}
                            data-testid={`rejustify-absence-${att.date}`}
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            Re-justifier
                          </Button>
                        )}
                        {att.synthetic && (att.justification_status === 'pending' || att.justification_status === 'approved') && (
                          <span className="text-xs">{getJustificationBadge(att.justification_status)}</span>
                        )}
                        {/* Real attendance: late with no justification */}
                        {!att.synthetic && (att.is_late || att.is_absent) && !att.justification_file && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openJustifyDialog(att)}
                            data-testid={`justify-${att.id}`}
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            Justifier
                          </Button>
                        )}
                        {!att.synthetic && att.justification_file && (
                          <span className="text-xs">{getJustificationBadge(att.justification_status)}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedAttendance?.synthetic ? "Justifier l'absence" : 'Justifier Retard/Absence'}
            </DialogTitle>
            <DialogDescription>
              Date : {selectedAttendance?.date}
              {selectedAttendance?.synthetic && (
                <span className="block mt-1 text-xs text-slate-500">
                  Aucun pointage enregistré ce jour. Expliquez la raison de votre absence.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadJustification} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file">
                Fichier (PDF ou Image) {selectedAttendance?.synthetic ? <span className="text-slate-400 font-normal">(optionnel)</span> : '*'}
              </Label>
              <Input
                id="file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files[0])}
                required={!selectedAttendance?.synthetic}
                data-testid="file-input"
              />
              <p className="text-xs text-slate-500">
                Formats acceptés : PDF, JPG, PNG
                {selectedAttendance?.synthetic && ' — joignez un certificat médical, attestation, etc. si applicable.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Motif *</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                rows={3}
                placeholder={selectedAttendance?.synthetic
                  ? "Ex: Maladie, certificat médical fourni au RH..."
                  : "Expliquez la raison de votre retard/absence..."}
                data-testid="reason-input"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" className="flex-1 bg-primary" data-testid="submit-justification">
                Envoyer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}